import { changePasswordSchema, PASSWORD_MIN_LENGTH, ROLE_DESCRIPTIONS, ROLE_LABELS } from '@edms/contracts';
import { AlertCircle, KeyRound, ShieldCheck } from 'lucide-react';
import { type FormEvent, useState } from 'react';

import { PageBody, PageHeader } from '@/components/page-header';
import { useToast } from '@/components/ui/overlays';
import { Button, Field, Input } from '@/components/ui/primitives';
import { Badge, Card, SectionHeader } from '@/components/ui/surfaces';
import { useAuth } from '@/features/auth/auth-context';
import { api, ApiError } from '@/lib/api-client';
import { formatDateTime } from '@/lib/utils';

/**
 * Compte personnel.
 *
 * En mode `forced`, l'écran est le seul accessible : un mot de passe temporaire
 * transmis hors ligne doit être renouvelé avant tout accès aux archives (§16).
 * Le formulaire l'annonce explicitement plutôt que de laisser l'utilisateur
 * buter sur des redirections.
 */
export function AccountPage({ forced = false }: { forced?: boolean }): React.JSX.Element {
  const { user, refreshUser, logout } = useAuth();
  const toast = useToast();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);

    const parsed = changePasswordSchema.safeParse({ currentPassword, newPassword, confirmPassword });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Saisie invalide.');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/auth/change-password', parsed.data);
      toast.success(
        'Mot de passe modifié',
        'Vos autres sessions ont été fermées. Reconnectez-vous.',
      );
      // Le serveur a révoqué toutes les sessions, y compris celle-ci : rester
      // sur place afficherait une interface qui échouerait à la requête suivante.
      await logout();
    } catch (cause) {
      setError(
        cause instanceof ApiError ? cause.message : 'Modification impossible. Réessayez.',
      );
      await refreshUser().catch(() => undefined);
    } finally {
      setSubmitting(false);
    }
  };

  const content = (
    <div className="mx-auto max-w-2xl space-y-5">
      {forced && (
        <div className="flex items-start gap-3 rounded-lg border border-warning-border bg-warning-soft px-4 py-3">
          <ShieldCheck className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />
          <div>
            <p className="text-sm font-medium text-ink">Renouvellement obligatoire</p>
            <p className="mt-0.5 text-xs leading-relaxed text-ink-muted">
              Votre mot de passe a été défini par un administrateur. Choisissez-en un nouveau, connu
              de vous seul, pour accéder à l'application.
            </p>
          </div>
        </div>
      )}

      {user && (
        <Card>
          <SectionHeader title="Mon profil" />
          <dl className="mt-4 grid gap-x-6 gap-y-3 sm:grid-cols-2">
            <Entry label="Nom" value={user.fullName} />
            <Entry label="Adresse e-mail" value={user.email} />
            <Entry label="Bureau" value={`${user.branchCode} — ${user.branchName}`} />
            <Entry label="Dernière connexion" value={formatDateTime(user.lastLoginAt)} />
            <div className="sm:col-span-2">
              <dt className="text-2xs text-ink-faint">Rôle</dt>
              <dd className="mt-1 flex flex-wrap items-center gap-2">
                <Badge tone="accent">{ROLE_LABELS[user.role]}</Badge>
                <span className="text-xs text-ink-muted">{ROLE_DESCRIPTIONS[user.role]}</span>
              </dd>
            </div>
          </dl>
        </Card>
      )}

      <Card>
        <SectionHeader
          title="Changer mon mot de passe"
          description={`Au moins ${PASSWORD_MIN_LENGTH} caractères, avec majuscule, minuscule et chiffre.`}
        />

        <form onSubmit={(event) => void onSubmit(event)} className="mt-4 space-y-4" noValidate>
          {error && (
            <div
              role="alert"
              className="flex items-start gap-2.5 rounded-md border border-danger-border bg-danger-soft px-3 py-2.5"
            >
              <AlertCircle className="mt-0.5 size-4 shrink-0 text-danger" aria-hidden />
              <p className="text-xs leading-relaxed text-danger">{error}</p>
            </div>
          )}

          <Field label="Mot de passe actuel" htmlFor="current-password" required>
            <Input
              id="current-password"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
            />
          </Field>

          <Field label="Nouveau mot de passe" htmlFor="new-password" required>
            <Input
              id="new-password"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
            />
          </Field>

          <Field label="Confirmation" htmlFor="confirm-password" required>
            <Input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
            />
          </Field>

          <div className="flex justify-end">
            <Button
              type="submit"
              variant="primary"
              loading={submitting}
              icon={<KeyRound className="size-4" />}
            >
              Modifier le mot de passe
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );

  if (forced) {
    return (
      <div className="min-h-full bg-canvas px-4 py-10">
        <div className="mx-auto max-w-2xl">
          <h1 className="mb-6 text-center text-lg font-semibold tracking-tight text-ink">
            Sécurisation de votre compte
          </h1>
          {content}
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHeader title="Mon compte" description="Profil, rôle et sécurité de votre accès" />
      <PageBody>{content}</PageBody>
    </>
  );
}

function Entry({ label, value }: { label: string; value: string }): React.JSX.Element {
  return (
    <div>
      <dt className="text-2xs text-ink-faint">{label}</dt>
      <dd className="mt-0.5 text-sm text-ink">{value}</dd>
    </div>
  );
}
